import * as z from "zod";
import {
  DEFAULT_TCR_BRAND_FIELDS,
  DEFAULT_TCR_CAMPAIGN_FIELDS,
  TcrBrandFieldsSchema,
  TcrCampaignFieldsSchema,
} from "../shared/tcr";

// =============================================================================
// Business info (step: rec-business-info)
// =============================================================================

export const RecBusinessInfoObjectSchema = z.object({
  legalEntityName: z.string().min(1).optional(),
  ein: z
    .string()
    .regex(/^\d{9}$/)
    .optional(),
  brandName: z.string().min(1).optional(),
  supportEmail: z.string().email().optional(),
  ...TcrBrandFieldsSchema.shape,
});
export const RecBusinessInfoSchema: z.ZodType<RecBusinessInfo> =
  RecBusinessInfoObjectSchema;
export interface RecBusinessInfo extends z.infer<
  typeof RecBusinessInfoObjectSchema
> {}

// =============================================================================
// Messaging config (step: rec-messaging)
// =============================================================================

export const RecDiscountTypes = [
  "percent",
  "free-shipping",
  "fixed",
  "free-gift",
] as const;
export type RecDiscountType = (typeof RecDiscountTypes)[number];

const RecMessagingSchema_ = z.object({
  repName: z.string().min(1).optional(),
  brandName: z.string().min(1).optional(),
  outreachTemplate: z.string().min(1).optional(),
  discountType: z.enum(RecDiscountTypes).optional(),
  discountValue: z.number().nonnegative().optional(),
  ...TcrCampaignFieldsSchema.shape,
});
export const RecMessagingSchema: z.ZodType<RecMessaging> = RecMessagingSchema_;
export interface RecMessaging extends z.infer<typeof RecMessagingSchema_> {}

// =============================================================================
// Full completion payload (assembled server-side from CRDB form_data)
// =============================================================================

const RecoverCompletionPayloadSchema_ = z.object({
  businessInfo: RecBusinessInfoSchema,
  messaging: RecMessagingSchema,
});
export const RecoverCompletionPayloadSchema: z.ZodType<RecoverCompletionPayload> =
  RecoverCompletionPayloadSchema_;
export interface RecoverCompletionPayload extends z.infer<
  typeof RecoverCompletionPayloadSchema_
> {}

// =============================================================================
// Product availability (returned by getProgress, consumed by steps)
// =============================================================================

/**
 * Required team profile fields for Recover phone number provisioning that
 * may be missing from the merchant's team document. The rec-business-info
 * step renders inline inputs for any field listed here so the merchant can
 * fill in the gap before provisioning fires.
 */
export const RecoverMissingMerchantField = z.enum([
  "street1",
  "city",
  "state",
  "zip",
  "country",
  "adminName",
  "adminPhone",
]);
export type RecoverMissingMerchantField = z.infer<
  typeof RecoverMissingMerchantField
>;

export const RecoverPhoneNumberProvisioningStatus = z.enum([
  "none",
  "pending",
  "approved",
  "rejected",
]);
export type RecoverPhoneNumberProvisioningStatus = z.infer<
  typeof RecoverPhoneNumberProvisioningStatus
>;

export const RecoverMerchantProfileSchema = z.object({
  street1: z.string().nullish(),
  street2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  country: z.string().nullish(),
  adminName: z.string().nullish(),
  adminPhone: z.string().nullish(),
});
export type RecoverMerchantProfile = z.infer<
  typeof RecoverMerchantProfileSchema
>;

const RecoverAvailabilitySchema_ = z.object({
  /** Merchant already has a Recover-compatible Shopify billing plan active. */
  hasShopifyBilling: z.boolean(),
  /** settings.recover.enabled in team settings (activates the landing-page "active" state). */
  recoverEnabled: z.boolean(),
  /** Team already has Conversion on — skip the intent add-on steps entirely. */
  intentAlreadyEnabled: z.boolean().default(false),
  /** Carrier provisioning status for the dedicated Recover phone number. */
  phoneNumberProvisioningStatus: RecoverPhoneNumberProvisioningStatus,
  /**
   * Fine-grained stage status for the three-stage merchant progress UI
   * shown while `phoneNumberProvisioningStatus === "pending"`. "none"
   * means the stage hasn't started or isn't relevant yet.
   */
  brandApprovalStatus: z
    .enum(["none", "pending", "verified", "rejected"])
    .default("none"),
  campaignApprovalStatus: z
    .enum(["none", "pending", "verified", "rejected"])
    .default("none"),
  /**
   * The dedicated Recover phone number (E.164) once provisioning has
   * completed. Null while pending or rejected.
   */
  phoneNumber: z.string().nullish(),
  /**
   * Most recent provisioning failure reason from the workflow, if
   * `phoneNumberProvisioningStatus === "rejected"`. Surfaced on the
   * rec-phone-number step UI for the merchant.
   */
  phoneNumberLastError: z.string().nullish(),
  /**
   * Required team profile fields the merchant still needs to provide
   * before phone number provisioning can fire. Empty array means the team
   * has everything needed and the rec-business-info step renders only its
   * standard fields.
   */
  missingMerchantFields: z.array(RecoverMissingMerchantField),
  /**
   * Existing team profile values pre-populated for the rec-business-info
   * step so the merchant can review and edit rather than re-enter from scratch.
   */
  merchantProfile: RecoverMerchantProfileSchema,
});
export const RecoverAvailabilitySchema: z.ZodType<RecoverAvailability> =
  RecoverAvailabilitySchema_;
export interface RecoverAvailability extends z.infer<
  typeof RecoverAvailabilitySchema_
> {}

// =============================================================================
// Default values (used by useStepFormData fallback + assemblePayload merge)
// =============================================================================

export const DEFAULT_RECOVER_OUTREACH_TEMPLATE =
  "Hey {first_name}, this is {rep_name} from {brand_name}. Saw you were checking out {product}. Want me to see if I can get a discount for you? Reply YES and I'll send one. {discount_code}";

export const DEFAULT_BUSINESS_INFO: RecBusinessInfo = {
  legalEntityName: "",
  ein: undefined,
  brandName: "",
  supportEmail: undefined,
  ...DEFAULT_TCR_BRAND_FIELDS,
};

export const DEFAULT_MESSAGING: RecMessaging = {
  repName: "Jessica",
  brandName: "",
  outreachTemplate: DEFAULT_RECOVER_OUTREACH_TEMPLATE,
  discountType: "percent",
  discountValue: 15,
  ...DEFAULT_TCR_CAMPAIGN_FIELDS,
};
